import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import provideHook from "@/lib/provideHook";

import type { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";

const statusOrder: AppointmentStatus[] = [
  "pending",
  "accepted",
  "running",
  "completed",
  "cancelled",
  "lost_opportunity"
];
type TimeField =
  | "accepted_time"
  | "dispatch_time"
  | "arrival_time"
  | "departure_time";

type AppointmentTimes = Partial<Record<TimeField, string>>;

type StatusRequiredTimes = Record<AppointmentStatus, TimeField[]>;
type StatusTimeLosses = Record<
  AppointmentStatus,
  Partial<Record<AppointmentStatus, TimeField[]>>
>;

const APPOINTMENT_STATUS_REQUIRED_TIMES: StatusRequiredTimes = {
  pending: [],
  accepted: ["accepted_time", "dispatch_time"],
  running: ["accepted_time", "dispatch_time", "arrival_time"],
  completed: [
    "accepted_time",
    "dispatch_time",
    "arrival_time",
    "departure_time"
  ],
  cancelled: [
    "accepted_time",
    "dispatch_time",
    "arrival_time",
    "departure_time"
  ],
  lost_opportunity: [] // Adjust if needed
};

const APPOINTMENT_STATUS_TIME_LOSSES: StatusTimeLosses = {
  pending: {},
  accepted: {
    pending: ["accepted_time", "dispatch_time"]
  },
  running: {
    pending: ["accepted_time", "dispatch_time", "arrival_time"],
    accepted: ["arrival_time"]
  },
  completed: {
    pending: [
      "accepted_time",
      "dispatch_time",
      "arrival_time",
      "departure_time"
    ],
    accepted: ["arrival_time", "departure_time"],
    running: ["departure_time"]
  },
  cancelled: {
    pending: [
      "accepted_time",
      "dispatch_time",
      "arrival_time",
      "departure_time"
    ],
    accepted: ["arrival_time", "departure_time"],
    running: ["departure_time"]
  },
  lost_opportunity: {}
};

interface ChangeValidationResult {
  success: boolean;
  missing?: TimeField;
}
export const useAppointmentStatus = provideHook(
  (appointment: AppointmentV2) => {
    const store = useStore();

    const status = ref<AppointmentStatus>(appointment.status);
    const displayStatus = computed(
      () => appointment.display_status || appointment.status
    );

    const times = reactive<AppointmentTimes>({
      accepted_time: appointment.accepted_time || "",
      dispatch_time: appointment.dispatch_time || "",
      arrival_time: appointment.arrival_time || "",
      departure_time: appointment.departure_time || ""
    });

    function getRequiredTimesFor(newStatus: AppointmentStatus): TimeField[] {
      return APPOINTMENT_STATUS_REQUIRED_TIMES[newStatus] || [];
    }

    function getRequiredFieldsForStatus(
      toStatus: AppointmentStatus
    ): TimeField[] {
      return APPOINTMENT_STATUS_REQUIRED_TIMES[toStatus] || [];
    }

    function getTimesToRemove(
      fromStatus: AppointmentStatus,
      toStatus: AppointmentStatus
    ): TimeField[] {
      if (!isBackwardTransition(fromStatus, toStatus)) return [];
      return APPOINTMENT_STATUS_TIME_LOSSES[fromStatus]?.[toStatus] || [];
    }

    function canChangeStatus(
      toStatus: AppointmentStatus,
      newTimes: AppointmentTimes
    ): ChangeValidationResult {
      const required = getRequiredTimesFor(toStatus);
      for (const key of required) {
        if (!newTimes[key]) {
          return {
            success: false,
            missing: key
          };
        }
      }
      return { success: true };
    }

    function isBackwardTransition(
      from: AppointmentStatus,
      to: AppointmentStatus
    ): boolean {
      return statusOrder.indexOf(to) < statusOrder.indexOf(from);
    }

    async function changeStatus(
      toStatus: AppointmentStatus,
      newTimes: AppointmentTimes,
      reason: string,
      link: { href: string; method: string }
    ): Promise<void> {
      // Apply new times
      for (const [key, value] of Object.entries(newTimes)) {
        (times as any)[key] = value;
      }

      // Remove outdated times
      const toRemove = getTimesToRemove(status.value, toStatus);
      for (const key of toRemove) {
        delete times[key as TimeField];
      }

      status.value = toStatus;

      // Dispatch to Vuex store
      await store.dispatch("updateAppointmentStatusAndTimes", {
        link,
        params: {
          ...times,
          reason
        }
      });
    }

    return {
      status,
      displayStatus,
      times,
      canChangeStatus,
      changeStatus,
      getRequiredFieldsForStatus,
      getTimesToRemove
    };
  }
);
